import { useState } from "react";
import { ArrowRight } from "lucide-react";

import { AdjustmentTableCard } from "@/components/adjustments/AdjustmentTableCard";
import { Button } from "@/components/ui/button";
import {
  mockNetDebtAdjustments,
  mockQoEAdjustments,
  mockQoNWCAdjustments,
  type AdjustmentItem,
  type AdjustmentTable,
} from "@/data/mockAdjustments";

interface AdjustmentSummaryStageProps {
  active: boolean;
  onContinue: () => void;
}

const initialTables = [mockQoEAdjustments, mockQoNWCAdjustments, mockNetDebtAdjustments];

export function AdjustmentSummaryStage({ active, onContinue }: AdjustmentSummaryStageProps) {
  const [tables, setTables] = useState<AdjustmentTable[]>(initialTables);
  const [pasteTableId, setPasteTableId] = useState<string | null>(null);
  const [pasteText, setPasteText] = useState("");

  const updateItem = (tableId: string, itemId: string, update: (item: AdjustmentItem) => AdjustmentItem) => {
    setTables((current) => current.map((table) => table.id === tableId
      ? { ...table, items: table.items.map((item) => item.id === itemId ? update(item) : item) }
      : table));
  };

  const handleValueChange = (
    tableId: string,
    itemId: string,
    period: string,
    value: number | null,
    monthKey?: string,
  ) => {
    updateItem(tableId, itemId, (item) => {
      if (period === "__label__" && monthKey) return { ...item, label: monthKey };
      if (period === "__account__" && monthKey) return { ...item, account: monthKey, subAccount: "" };
      if (period === "__subaccount__" && monthKey) return { ...item, subAccount: monthKey };
      if (monthKey && item.monthlyBreakdown?.[period]) {
        const breakdown = item.monthlyBreakdown[period].map((month) => month.month === monthKey
          ? { ...month, value }
          : month);
        const total = breakdown.reduce((sum, month) => sum + (month.value ?? 0), 0);
        return {
          ...item,
          monthlyBreakdown: { ...item.monthlyBreakdown, [period]: breakdown },
          values: { ...item.values, [period]: total || null },
        };
      }
      return { ...item, values: { ...item.values, [period]: value } };
    });
  };

  const handleAdd = (tableId: string) => {
    setTables((current) => current.map((table) => {
      if (table.id !== tableId) return table;
      const item: AdjustmentItem = {
        id: `${tableId}-manual-${Date.now()}`,
        label: "New adjustment",
        refNumber: table.items.filter((row) => row.refNumber).length + 1,
        values: Object.fromEntries(table.periods.map((period) => [period, null])),
        status: "pending",
      };
      const sectionIndex = table.items.findIndex((row) => row.isSectionHeader);
      const insertIndex = table.items.findIndex((row, index) =>
        index > sectionIndex && (row.label.toLowerCase().includes("total") || row.isSubtotal));
      const items = [...table.items];
      items.splice(insertIndex < 0 ? items.length : insertIndex, 0, item);
      return { ...table, items };
    }));
  };

  const handlePaste = () => {
    if (!pasteTableId || !pasteText.trim()) return;
    setTables((current) => current.map((table) => {
      if (table.id !== pasteTableId) return table;
      const pasted: AdjustmentItem[] = pasteText.trim().split("\n").filter((line) => line.trim()).map((line, index) => {
        const cells = line.split("\t");
        const values: AdjustmentItem["values"] = {};
        table.periods.forEach((period, periodIndex) => {
          const raw = cells[periodIndex + 1]?.trim();
          const parsed = Number(raw?.replace(/[,$\s()]/g, ""));
          values[period] = raw && Number.isFinite(parsed)
            ? (raw.includes("(") ? -Math.abs(parsed) : parsed)
            : null;
        });
        return {
          id: `${table.id}-paste-${Date.now()}-${index}`,
          label: cells[0]?.trim() || `Pasted adjustment ${index + 1}`,
          refNumber: table.items.filter((row) => row.refNumber).length + index + 1,
          values,
          status: "pending",
          isNew: true,
          reason: "Manually pasted from Excel",
        };
      });
      const items = [...table.items];
      const totalIndex = items.findIndex((row) => row.label.toLowerCase().includes("total adjustment"));
      items.splice(totalIndex < 0 ? items.length : totalIndex, 0, ...pasted);
      return { ...table, items };
    }));
    setPasteTableId(null);
    setPasteText("");
  };

  const pendingCount = tables.reduce((count, table) => count + table.items.filter((item) =>
    item.status === "pending" && !item.isSectionHeader && !item.isSubtotal).length, 0);

  return (
    <section aria-label="Adjustment Summary" className={active ? "min-h-0 flex-1 overflow-auto" : "hidden"}>
      <div className="builder-stage-frame space-y-6 py-5 lg:py-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold">Adjustment Summary</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Reported EBITDA <span className="font-mono font-medium text-foreground">$1,380k</span>
              <span className="mx-1.5">→</span>
              Normalised EBITDA <span className="font-mono font-medium text-foreground">$80k</span>
              <span className="ml-1.5 text-xs text-rose-500">(-94%)</span>
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            <Button variant="outline" size="sm" disabled={pendingCount === 0} onClick={() =>
              setTables((current) => current.map((table) => ({
                ...table,
                items: table.items.map((item) => !item.isSectionHeader && !item.isSubtotal
                  ? { ...item, status: "accepted" }
                  : item),
              })))}>
              Accept All Pending
            </Button>
            <Button size="sm" disabled={pendingCount > 0} onClick={onContinue}>
              Continue <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        <div className="space-y-5">
          {tables.map((table) => (
            <AdjustmentTableCard
              key={table.id}
              table={table}
              onAccept={(tableId, itemId) => updateItem(tableId, itemId, (item) => ({ ...item, status: "accepted" }))}
              onReject={(tableId, itemId) => updateItem(tableId, itemId, (item) => ({ ...item, status: "rejected" }))}
              onValueChange={handleValueChange}
              onAddAdjustment={() => handleAdd(table.id)}
              onPasteFromExcel={() => { setPasteTableId(table.id); setPasteText(""); }}
            />
          ))}
        </div>

        {pasteTableId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setPasteTableId(null)}>
            <div className="mx-4 w-full max-w-lg rounded-xl border border-border bg-background p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
              <h3 className="mb-1 text-sm font-semibold">Paste from Excel</h3>
              <p className="mb-3 text-xs text-muted-foreground">
                Copy rows from Excel and paste below. Format: <span className="bg-muted px-1 font-mono">Label → {tables.find((table) => table.id === pasteTableId)?.periods.join(" → ")}</span>
              </p>
              <textarea
                value={pasteText}
                onChange={(event) => setPasteText(event.target.value)}
                placeholder={"Paste here (Ctrl+V)...\n\nExample:\nDirector bonus removal\t-50\t-75\t-60\nConsulting fees\t-30\t-45\t-25"}
                className="h-32 w-full resize-none rounded-md border border-border bg-muted/50 px-3 py-2 font-mono text-xs outline-none focus:border-primary/40"
                autoFocus
              />
              {pasteText && <p className="mt-2 text-[10px] text-muted-foreground">{pasteText.trim().split("\n").filter((line) => line.trim()).length} row(s) detected</p>}
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setPasteTableId(null)}>Cancel</Button>
                <Button size="sm" disabled={!pasteText.trim()} onClick={handlePaste}>Import {pasteText.trim().split("\n").filter((line) => line.trim()).length || 0} row(s)</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
